import { useCallback, useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { useThemeStore } from "../../../../stores/theme";
import type { DockKindProps } from "../../registry";
import { useTerminalSocket } from "./useTerminalSocket";
import styles from "./TerminalPanel.module.css";

/**
 * The terminal panel body (2026-07-15) — xterm.js over a PTY WebSocket.
 *
 * Theme colours are READ FROM THE LIVE CSS VARIABLES rather than hardcoded.
 * There are three themes (dark / light / zinc) and the design system's rule is
 * that no component hardcodes a colour; a terminal is the easiest place in the
 * app to forget that, because xterm wants a JS object rather than a stylesheet.
 * `getComputedStyle` is the bridge, and the theme is rebuilt when the store's
 * theme changes so a switch doesn't leave a dark terminal on a light page.
 */

/** Read a design-system token's live value. Falls back only if the var is
 *  genuinely absent (a token rename), never to a hardcoded brand colour. */
function cssVar(el: HTMLElement, name: string, fallback: string): string {
	const v = getComputedStyle(el).getPropertyValue(name).trim();
	return v || fallback;
}

function buildTheme(host: HTMLElement) {
	return {
		background: cssVar(host, "--bg-primary", "#0a0a0f"),
		foreground: cssVar(host, "--text-primary", "#e5e7eb"),
		cursor: cssVar(host, "--accent", "#818cf8"),
		cursorAccent: cssVar(host, "--bg-primary", "#0a0a0f"),
		selectionBackground: cssVar(host, "--border", "#3f3f46"),
	};
}

export function TerminalPanel({ panel }: DockKindProps) {
	const hostRef = useRef<HTMLDivElement | null>(null);
	const fitRef = useRef<FitAddon | null>(null);
	const [term, setTerm] = useState<Terminal | null>(null);
	const theme = useThemeStore((s) => s.theme);
	const [size, setSize] = useState({ rows: 24, cols: 80 });

	const { status, sendResize } = useTerminalSocket({ term, rows: size.rows, cols: size.cols });

	// Create the terminal once per panel.
	useEffect(() => {
		const host = hostRef.current;
		if (!host) return;
		const t = new Terminal({
			fontFamily: cssVar(host, "--font-mono", "monospace"),
			fontSize: 13,
			cursorBlink: true,
			// Scrollback costs memory per panel and the dock can hold several.
			// 2000 lines is a generous session and a bounded one.
			scrollback: 2000,
			theme: buildTheme(host),
			allowProposedApi: true,
		});
		const fit = new FitAddon();
		t.loadAddon(fit);
		t.open(host);
		fitRef.current = fit;
		try {
			fit.fit();
			setSize({ rows: t.rows, cols: t.cols });
		} catch {
			/* zero-sized container on first paint — the observer below re-fits */
		}
		setTerm(t);
		return () => {
			t.dispose();
			fitRef.current = null;
		};
	}, []);

	// Re-theme in place on a theme switch. Disposing and recreating the terminal
	// would wipe the scrollback — the user's session — for a colour change.
	useEffect(() => {
		const host = hostRef.current;
		if (!term || !host) return;
		term.options.theme = buildTheme(host);
	}, [theme, term]);

	// Refit when the dock card resizes, and tell the shell.
	//
	// rAF-throttled on purpose: the dock's seam drag fires pointermove at screen
	// rate, and `fit()` measures + reflows the whole grid. Unthrottled, the fit
	// fights the drag and the card judders.
	const refit = useCallback(() => {
		const fit = fitRef.current;
		if (!fit || !term) return;
		try {
			fit.fit();
		} catch {
			return;
		}
		if (term.rows !== size.rows || term.cols !== size.cols) {
			setSize({ rows: term.rows, cols: term.cols });
			sendResize(term.rows, term.cols);
		}
	}, [term, size.rows, size.cols, sendResize]);

	useEffect(() => {
		const host = hostRef.current;
		if (!host || !term) return;
		let frame = 0;
		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(refit);
		});
		ro.observe(host);
		return () => {
			cancelAnimationFrame(frame);
			ro.disconnect();
		};
	}, [term, refit]);

	return (
		<div className={styles.panel} data-panel-id={panel.id}>
			<div ref={hostRef} className={styles.screen} />
			{status.state === "error" && (
				// Verbatim, not paraphrased: a contract refusal names the exact line
				// to add to CONTRACT.md. "Terminal unavailable" would throw away the
				// only part of the message that helps.
				<div className={styles.overlay} role="alert">
					<p className={styles.errTitle}>Terminal unavailable</p>
					<p className={styles.errDetail}>{status.detail}</p>
				</div>
			)}
			{status.state === "exited" && (
				<div className={styles.overlay} role="status">
					<p className={styles.errTitle}>Shell exited</p>
					<p className={styles.errDetail}>Close this panel and open a new terminal to start again.</p>
				</div>
			)}
		</div>
	);
}
